import { useEffect, useMemo, useState } from 'react'
import { useUserAdmin } from '../hooks/useUserAdmin'
import { ResetPasswordDialog } from '../components/ResetPasswordDialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ChevronLeft, ChevronRight, KeyRound, Search, Users, RefreshCw } from 'lucide-react'
import type { User } from '@/types/entities'
import { UserRole } from '@/types/entities'

const PAGE_SIZE = 25
const ANY_ROLE = 'all'

export function UserAdminPage() {
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [role, setRole] = useState<string>(ANY_ROLE)
  const [page, setPage] = useState(0)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false)

  // Each keystroke is now a request, so wait for a pause before making one.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300)
    return () => clearTimeout(t)
  }, [searchInput])

  // Page 4 of an old filter is meaningless under a new one, and often past the end of it.
  useEffect(() => {
    setPage(0)
  }, [search, role])

  const { users, total, isLoading, isFetching, refetch, resetPassword, isResettingPassword } =
    useUserAdmin({
      search,
      role: role === ANY_ROLE ? undefined : (role as UserRole),
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    })

  const { firstShown, lastShown, hasPrev, hasNext } = useMemo(() => {
    const first = total === 0 ? 0 : page * PAGE_SIZE + 1
    return {
      firstShown: first,
      lastShown: Math.min((page + 1) * PAGE_SIZE, total),
      hasPrev: page > 0,
      hasNext: (page + 1) * PAGE_SIZE < total,
    }
  }, [page, total])

  const handleResetPassword = (userId: number, newPassword: string) => {
    resetPassword({ userId, newPassword })
  }

  const openResetDialog = (user: User) => {
    setSelectedUser(user)
    setIsResetDialogOpen(true)
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  return (
    <div className="space-y-8 p-8">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="font-serif text-4xl font-semibold tracking-tight">User Administration</h1>
          <p className="text-muted-foreground text-base">
            Manage users and reset passwords
          </p>
        </div>
        <Button variant="outline" onClick={() => refetch()}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                {search || role !== ANY_ROLE ? 'Matching users' : 'All users'}
              </CardTitle>
              <CardDescription>
                {total === 0
                  ? 'No users match'
                  : `Showing ${firstShown}–${lastShown} of ${total}`}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY_ROLE}>Any role</SelectItem>
                  <SelectItem value={UserRole.ARTIST}>Artists</SelectItem>
                  <SelectItem value={UserRole.MANAGER}>Managers</SelectItem>
                </SelectContent>
              </Select>
              <div className="relative w-72">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <div className="flex h-64 items-center justify-center">
              <p className="text-muted-foreground">Loading users...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="flex h-64 items-center justify-center">
              <p className="text-muted-foreground">
                {search || role !== ANY_ROLE ? 'No users match your search' : 'No users found'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Login</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">
                          {user.firstName} {user.lastName}
                        </p>
                        {user.artisticName && (
                          <p className="text-sm text-muted-foreground">{user.artisticName}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Badge variant={user.role === UserRole.MANAGER ? 'default' : 'secondary'}>
                        {user.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={user.verified ? 'default' : 'outline'}>
                        {user.verified ? 'Verified' : 'Unverified'}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatDate(user.dateLastLogin)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openResetDialog(user)}
                      >
                        <KeyRound className="mr-2 h-4 w-4" />
                        Reset Password
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {(hasPrev || hasNext) && (
            <div className="flex items-center justify-between border-t pt-4">
              <p className="text-sm text-muted-foreground">
                {isFetching ? 'Loading…' : `Page ${page + 1} of ${Math.ceil(total / PAGE_SIZE)}`}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!hasPrev || isFetching}
                  onClick={() => setPage((p) => Math.max(p - 1, 0))}
                >
                  <ChevronLeft className="mr-1 h-4 w-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!hasNext || isFetching}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <ResetPasswordDialog
        user={selectedUser}
        open={isResetDialogOpen}
        onOpenChange={setIsResetDialogOpen}
        onSubmit={handleResetPassword}
        isSubmitting={isResettingPassword}
      />
    </div>
  )
}
